import type { FindingStatus } from '@/graphql/generated/graphql'
import { categoryAnchor } from './tone'

/**
 * Turns the flat finding list into sections, without sorting it.
 *
 * The schema is explicit that the order it sends is meaningful: category, then
 * failures before unknowns before passes, then worst severity first, then id,
 * subject and title alphabetically. It asks a client to "render the list as it
 * arrives rather than sorting it again". So this file never calls `sort`, never
 * builds a lookup keyed by category and reads it back in some other order, and
 * never partitions the array into buckets that get concatenated. It makes one
 * left-to-right pass and emits what it sees.
 *
 * Two consequences of that discipline are worth naming.
 *
 * A category boundary is detected by the value changing from one element to the
 * next, not by grouping into a map. If a category ever appeared twice with
 * something else in between, this produces two sections rather than merging
 * them, because merging would move findings past each other.
 *
 * Passing findings collapse into a run, and a run is a maximal sequence of
 * consecutive passes. In practice the canonical order puts every pass in one
 * block at the end of its category, so a category yields one run. Nothing here
 * depends on that being true, and a pass that turned up in the middle would
 * stay in the middle rather than being swept to the bottom.
 *
 * While a scan is running the server keeps the whole list in canonical order,
 * so a finding can be inserted ahead of ones already rendered rather than only
 * appended. Sections are therefore rederived from the current array on every
 * poll rather than patched, which is a few microseconds for a report of a few
 * hundred findings and removes a whole class of incremental-update bug.
 */

export interface GroupableFinding {
  readonly key: string
  readonly category: string
  readonly status: FindingStatus
}

export interface SingleRow<F extends GroupableFinding> {
  readonly kind: 'finding'
  readonly id: string
  readonly finding: F
}

export interface PassRunRow<F extends GroupableFinding> {
  readonly kind: 'passes'
  /**
   * Stable across polls: it names the category and the ordinal of the run
   * inside it, so a disclosure the reader opened stays open when a finding is
   * inserted above it. Keying on the first pass's `key` would not survive that.
   */
  readonly id: string
  readonly findings: F[]
}

export type SectionRow<F extends GroupableFinding> = SingleRow<F> | PassRunRow<F>

export interface CategorySection<F extends GroupableFinding> {
  readonly category: string
  readonly anchor: string
  readonly rows: SectionRow<F>[]
  /** Counts over what this client actually received, not over the whole report. */
  readonly rendered: { total: number; failed: number; unknown: number; passed: number }
}

export function groupFindings<F extends GroupableFinding>(findings: readonly F[]): CategorySection<F>[] {
  const sections: CategorySection<F>[] = []
  let current: CategorySection<F> | null = null
  let openRun: F[] | null = null
  let runOrdinal = 0

  for (const finding of findings) {
    if (current === null || current.category !== finding.category) {
      current = {
        category: finding.category,
        anchor: categoryAnchor(finding.category),
        rows: [],
        rendered: { total: 0, failed: 0, unknown: 0, passed: 0 },
      }
      sections.push(current)
      openRun = null
      runOrdinal = 0
    }

    current.rendered.total += 1
    if (finding.status === 'FAIL') current.rendered.failed += 1
    else if (finding.status === 'PASS') current.rendered.passed += 1
    else current.rendered.unknown += 1

    if (finding.status === 'PASS') {
      if (openRun === null) {
        openRun = [finding]
        current.rows.push({
          kind: 'passes',
          id: `${finding.category}-passes-${runOrdinal}`,
          findings: openRun,
        })
        runOrdinal += 1
      } else {
        openRun.push(finding)
      }
      continue
    }

    openRun = null
    current.rows.push({ kind: 'finding', id: finding.key, finding })
  }

  return sections
}

/**
 * Whether the list this client holds is short of the whole report.
 *
 * The summary is always complete and always unfiltered, whatever `findings`
 * returned, which is the property that makes the cap on the list safe. It is
 * also the seam a future paywall runs through: the day the server starts
 * withholding findings, this is already the comparison that produces
 * "12 TLS problems, 3 shown", with no change to how it is computed.
 */
export function shortfall(renderedCount: number, summaryTotal: number): number {
  return Math.max(0, summaryTotal - renderedCount)
}
